import React, { ReactNode, useState } from 'react'
import { Button, Segment, Sidebar } from 'semantic-ui-react'
import styled from 'styled'

import AppFooter from 'components/AppFooter'
import AppHeader from 'components/AppHeader'
import AppSection from 'components/AppSection'
import Info from 'components/Info'
import AppSidebar from 'components/Sidebar'
import useAccount from 'hooks/useAccount'
import LayoutBase from 'layouts/LayoutBase'

interface InfoProps {
  accountName: string
  description?: string
  consumerConfidenceReportURL?: string
}

const InfoSection = ({
  accountName,
  description,
  consumerConfidenceReportURL
}: InfoProps) => {
  const [show, setShow] = useState<boolean>(true)
  const toggle = () => setShow(!show)
  const icon = show ? 'arrow left' : 'arrow right'
  return (
    <InfoContent style={!show ? { width: '50px' } : undefined}>
      <ToggleButton
        circular
        icon={icon}
        id="content-toggle-btn"
        onClick={toggle}
        style={show ? undefined : { opacity: 1 }}
        type="button"
      />
      {show && (
        <InfoCard
          accountName={accountName}
          description={description}
          consumerConfidenceReportURL={consumerConfidenceReportURL}
        />
      )}
    </InfoContent>
  )
}

export interface LayoutMainProps {
  children: ReactNode
  hasInfoSection?: boolean
  useInfoSidebar?: boolean
}

const LayoutMain = ({
  children,
  useInfoSidebar = false,
  ...rest
}: LayoutMainProps) => {
  const {
    accountName,
    description,
    logo,
    title,
    consumerConfidenceReportURL
  } = useAccount()
  const [visible, setVisible] = useState<boolean>(false)
  return (
    <Container {...rest}>
      <Sidebar.Pushable
        as={Segment}
        style={{ width: '100%', minHeight: '100vh', transform: 'none' }}>
        <AppSidebar setVisible={setVisible} visible={visible} />
        <Sidebar.Pusher
          dimmed={visible}
          style={{
            display: 'flex',
            flexDirection: 'column',
            minHeight: '100vh'
          }}>
          <SkipLink href="#content">Skip to content</SkipLink>
          <AppHeader logo={logo} title={title || accountName} />
          <Content id="content">
            <AppSection>
              {!useInfoSidebar && (
                <InfoSection
                  accountName={accountName}
                  description={description}
                  consumerConfidenceReportURL={consumerConfidenceReportURL}
                />
              )}
              <PageContent>{children}</PageContent>
            </AppSection>
          </Content>
          <AppFooter />
          {useInfoSidebar && (
            <InfoButton
              onClick={() => {
                setVisible(true)
                window.scrollTo({ top: 0, left: 0, behavior: 'smooth' })
              }}>
              <h6>info</h6>
            </InfoButton>
          )}
        </Sidebar.Pusher>
      </Sidebar.Pushable>
    </Container>
  )
}

const Container = styled(LayoutBase)``

const SkipLink = styled.a`
  position: absolute;
  top: 0;
  left: 50%;
  z-index: 50;
  transform: translateX(-50%);
  opacity: 0;
  pointer-events: none;

  &:focus {
    opacity: 1;
    pointer-events: all;
  }
`

const Content = styled.main`
  flex: 1;
  display: flex;
  flex-direction: row;
  background-color: ${p => p.theme.colors.black05};
  width: 100%;
`

const InfoContent = styled.section`
  margin-right: 2.5rem;
  height: 100%;
  min-width: 50px;
  max-width: 20%;
  position: relative;
`

const ToggleButton = styled(Button)`
  &&& {
    position: absolute;
    right: 12px;
    top: 12px;
  }
`

const InfoCard = styled(Info)`
  height: 100%;
`

const InfoButton = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #0033a0;
  position: fixed;
  border-radius: 30px;
  color: white;
  bottom: 17%;
  left: 10%;
  height: 50px;
  width: 50px;
  z-index: 10;
  cursor: pointer;

  @media only screen and (max-width: 1550px) {
    left: 3%;
  }

  & > h6 {
    margin: 10px 0;
  }
`

const PageContent = styled.section`
  flex: 3;
  flex-direction: column;
`

export default LayoutMain
